import { useQuery } from '@apollo/client';
import React, { useEffect, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PromptPhase, VoiceAgentEditablePromptFormData } from './ConfigSteps.tsx';
import { PromptInsertProvider } from './PromptInsertContext.tsx';
import { VoiceAgentPromptEditor } from './VoiceAgentPromptEditor.tsx';
import VoiceAgentPromptPhases, { FlowTemplate } from './VoiceAgentPromptPhases.tsx';
import VoiceAgentPromptSidePalette from './VoiceAgentPromptSidePalette.tsx';
import { Alert } from '../../../core/components/Alert.tsx';
import MultiSelectField from '../../../core/components/Fields/MultiSelectField.tsx';
import TextField from '../../../core/components/Fields/TextField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import SegmentedButton from '../../../core/components/SegmentedButton.tsx';
import {
    PROMPT_APPROACH_OPTIONS,
    PROMPT_DEFAULT_FLOW_TEMPLATE,
    PROMPT_FORMALITY_OPTIONS,
    PROMPT_GUARDRAILS,
} from '../constants.ts';
import { PROMPT_VARIABLES, toolsForPrompt } from '../promptContext.ts';
import { GET_VOICE_AGENT_EDITABLE_PROMPT_DEFAULTS } from '../queries.ts';
import { isEditablePromptEmpty, phasesFromTemplate } from '../utils/editablePrompt.ts';

interface SeededPrompt {
    language: string;
    role: string;
    phases: PromptPhase[];
}

const samePhases = (a: PromptPhase[], b: PromptPhase[]) => JSON.stringify(a) === JSON.stringify(b);

const Section = ({ title, helpText, children }: { title: string; helpText?: string; children: React.ReactNode }) => (
    <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-0.5">
            <h4 className="text-sm font-medium text-content-strong">{title}</h4>
            {helpText && <span className="text-xs text-content-medium">{helpText}</span>}
        </div>
        {children}
    </div>
);

// One of the two conversation-style controls: a segmented choice plus the footnote explaining the selected option
const StyleChoice = ({
    form,
    name,
    options,
}: {
    form: any;
    name: 'formality' | 'approach';
    options: readonly string[];
}) => {
    const { t } = useTranslation('voice_agents');
    const selected: string = form.watch(`editablePrompt.${name}`);
    return (
        <Controller
            name={`editablePrompt.${name}`}
            control={form.control}
            render={({ field }) => (
                <FormField label={t(`form.prompt.style.${name}.label`)}>
                    <div className="flex flex-col gap-1.5">
                        <SegmentedButton variant="joined" value={field.value} onValueChange={field.onChange}>
                            {options.map((option) => (
                                <SegmentedButton.Item key={option} value={option}>
                                    {t(`form.prompt.style.${name}.${option}`)}
                                </SegmentedButton.Item>
                            ))}
                        </SegmentedButton>
                        <span className="text-xs text-content-medium">
                            {t(`form.prompt.style.${name}.footnote.${selected || field.value}`)}
                        </span>
                    </div>
                </FormField>
            )}
        />
    );
};

// The Prompt tab (ENG-11790): the customer edits the editable part of the prompt — role, guardrail tags, conversation
// style, phases, remarks. The fixed parts (environment, rules, tool catalogue) are rendered by the backend and not
// shown here. In the create wizard (seedDefaults) the tab shows what the backend would instantiate anyway.
const VoiceAgentPromptFormFields = ({ form, seedDefaults = false }: { form: any; seedDefaults?: boolean }) => {
    const { t } = useTranslation('voice_agents');
    const formErrors = form.formState.errors?.editablePrompt || {};

    const language: string = form.watch('language') || 'de';
    const enabledTools: string[] = form.watch('enabledTools') || [];
    const transferPhoneNumber: string = form.watch('transferPhoneNumber') || '';
    const tools = toolsForPrompt(enabledTools, transferPhoneNumber);
    const editablePromptEnabled: boolean = form.watch('editablePromptEnabled');

    const { data } = useQuery(GET_VOICE_AGENT_EDITABLE_PROMPT_DEFAULTS, { variables: { language } });
    const defaults = data?.voiceAgentEditablePromptDefaults;
    const flowTemplates: FlowTemplate[] = defaults?.flowTemplates || [];

    // Show a new agent what it will get: filled once while nothing was authored or imported, and again when the
    // language changes while role and phases still equal the previous language's untouched template.
    const seeded = useRef<SeededPrompt | null>(null);
    useEffect(() => {
        if (!seedDefaults || !defaults) return;
        const current: VoiceAgentEditablePromptFormData = form.getValues('editablePrompt');
        const untouchedTemplate =
            seeded.current !== null &&
            seeded.current.language !== language &&
            seeded.current.role === current.role &&
            samePhases(seeded.current.phases, current.phases);
        if (!isEditablePromptEmpty(current) && !untouchedTemplate) return;
        const orderTemplate = (defaults.flowTemplates || []).find(
            (template: FlowTemplate) => template.key === PROMPT_DEFAULT_FLOW_TEMPLATE
        );
        const phases = phasesFromTemplate(orderTemplate?.phases);
        // reset with the merged values, as the wizard's config import does: it re-renders the field array and every
        // controller reliably, which separate setValue calls did not inside the modal
        form.reset({
            ...form.getValues(),
            editablePrompt: {
                ...current,
                role: defaults.role,
                phases,
                guardrails: seeded.current ? current.guardrails : defaults.defaultGuardrails,
            },
        });
        seeded.current = { language, role: defaults.role, phases };
    }, [defaults, language, seedDefaults, form]);

    return (
        <PromptInsertProvider>
            {!seedDefaults && editablePromptEnabled === false && (
                <Alert severity="warning">{t('form.prompt.legacyBanner')}</Alert>
            )}
            <div className="flex flex-col items-start gap-8 lg:flex-row">
                <div className="flex w-full min-w-0 flex-1 flex-col gap-8">
                    <Controller
                        name="editablePrompt.role"
                        control={form.control}
                        rules={{
                            validate: (value) =>
                                Boolean(value?.trim()) || (t('form.prompt.role.requiredError') as string),
                        }}
                        render={({ field }) => (
                            <FormField
                                label={t('form.prompt.role.label')}
                                required
                                error={formErrors.role}
                                helpText={t('form.prompt.role.helpText')}
                            >
                                <VoiceAgentPromptEditor
                                    value={field.value || ''}
                                    onValueChange={field.onChange}
                                    variables={PROMPT_VARIABLES}
                                    tools={tools}
                                    placeholder={t('form.prompt.role.placeholder')}
                                    minHeightClass="min-h-28"
                                    palette={false}
                                />
                            </FormField>
                        )}
                    />

                    <Section title={t('form.prompt.guardrails.title')} helpText={t('form.prompt.guardrails.helpText')}>
                        <Controller
                            name="editablePrompt.guardrails"
                            control={form.control}
                            render={({ field }) => (
                                <MultiSelectField
                                    value={field.value || []}
                                    onValueChange={field.onChange}
                                    onBlur={field.onBlur}
                                    options={PROMPT_GUARDRAILS.map((tag) => ({
                                        value: tag,
                                        label: t(`form.prompt.guardrails.tags.${tag}.label`),
                                        description: t(`form.prompt.guardrails.tags.${tag}.description`),
                                    }))}
                                    placeholder={t('form.prompt.guardrails.placeholder')}
                                    emptyMessage={t('form.prompt.guardrails.noneFound')}
                                    removeLabel={t('form.prompt.guardrails.remove')}
                                />
                            )}
                        />
                    </Section>

                    <Section title={t('form.prompt.style.title')}>
                        <StyleChoice form={form} name="formality" options={PROMPT_FORMALITY_OPTIONS} />
                        <StyleChoice form={form} name="approach" options={PROMPT_APPROACH_OPTIONS} />
                    </Section>

                    <VoiceAgentPromptPhases form={form} flowTemplates={flowTemplates} tools={tools} />

                    <Controller
                        name="editablePrompt.generalRemarks"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('form.prompt.remarks.label')}
                                error={formErrors.generalRemarks}
                                helpText={t('form.prompt.remarks.helpText')}
                            >
                                <TextField
                                    value={field.value || ''}
                                    onValueChange={field.onChange}
                                    onBlur={field.onBlur}
                                    placeholder={t('form.prompt.remarks.placeholder')}
                                    inputClassName="h-32"
                                />
                            </FormField>
                        )}
                    />
                </div>
                {/* one palette for every text on the tab: a column that sticks while the fields scroll, stacked below
                    them on narrow screens */}
                <aside className="w-full lg:sticky lg:top-4 lg:w-60 lg:flex-none">
                    <VoiceAgentPromptSidePalette variables={PROMPT_VARIABLES} tools={tools} />
                </aside>
            </div>
        </PromptInsertProvider>
    );
};

export default VoiceAgentPromptFormFields;
